import React, { useEffect, useState } from 'react';
import { fetchExhibitionNominations } from '../api'; 

const ExhibitionsTable = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // ---------------------------------------------------------------------------
  // DYNAMIC IMAGE URL CONFIGURATION
  // ---------------------------------------------------------------------------
  const isLocal = window.location.hostname === "localhost";
  const IMG_BASE_URL = isLocal 
    ? "http://localhost:5279/uploads/"           
    : "https://beamserver.nsfdcdigital.in/uploads/"; 

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setIsLoading(true);
    fetchExhibitionNominations()
      .then(res => {
        setData(res.data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Error fetching nominations:", err);
        setIsLoading(false);
      });
  };

  // Helper to open document/image in new tab
  const viewDocument = (filename) => {
    if (!filename) return;
    window.open(`${IMG_BASE_URL}${filename}`, '_blank');
  };

  return (
    <div className="container-fluid mt-4 px-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="text-black fw-bold">Exhibition Nominations</h4>
        <span className="badge bg-secondary">{data.length} Records</span>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 text-muted">Loading Nominations...</p>
        </div>
      ) : (
        <div className="table-responsive shadow-sm rounded" style={{ minHeight: '400px' }}>
          <table className="table table-hover table-striped table-bordered align-middle mb-0">
            <thead className="table-dark text-center align-middle">
              <tr>
                <th style={{width: '50px'}}>ID</th>
                <th style={{minWidth: '200px'}}>Beneficiary Details</th>
                <th style={{minWidth: '150px'}}>Agency Source</th>
                <th style={{minWidth: '200px'}}>Product Details</th>
                <th>History</th>
                <th style={{minWidth: '140px'}}>Documents</th>
                <th style={{minWidth: '150px'}}>Product Photos</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item.id || index}>
                  <td className="text-center fw-bold">{index + 1}</td>
                  
                  {/* 1. Beneficiary Details (Name, Gender, Mobile, District) */}
                  <td>
                    <div className="fw-bold text-primary">{item.beneficiaryName}</div>
                    <div className="small text-dark">
                      <span className="badge bg-light text-dark border me-1">{item.gender}</span>
                    </div>
                    <div className="small mt-1"><i className="bi bi-phone"></i> {item.mobileNumber}</div>
                    <div className="small text-muted"><i className="bi bi-geo-alt-fill text-danger"></i> {item.district}</div>
                  </td>
                  
                  {/* 2. Agency Source (Type & ID) */}
                  <td>
                    <span className="badge bg-info text-dark mb-1 text-wrap">{item.agencyType}</span>
                    <div className="small text-muted border-top pt-1 mt-1">
                      <strong>Ref ID:</strong><br/>
                      <span className="text-dark font-monospace">{item.loanArtisanId}</span>
                    </div>
                  </td>

                  {/* 3. Product Details (Category & Description) */}
                  <td>
                    <div className="fw-semibold text-success">{item.productCategory}</div>
                    <div 
                      className="text-muted small fst-italic mt-1" 
                      style={{maxWidth: '250px', whiteSpace: 'normal'}}
                    >
                      "{item.description}"
                    </div>
                  </td>

                  {/* 4. History (First Time vs Repeat) */}
                  <td className="text-center">
                    {item.isFirstTimeParticipant ? (
                      <span className="badge bg-success">First Time</span>
                    ) : (
                      <span className="badge bg-warning text-dark">
                        Repeat<br/>
                        <small className="fw-normal">Last: {item.lastParticipationDate ? new Date(item.lastParticipationDate).getFullYear() : 'Unknown'}</small>
                      </span>
                    )}
                  </td>

                  {/* 5. Uploaded Documents (Buttons) */}
                  <td>
                    <div className="d-grid gap-1">
                      {item.loanProofPath && (
                        <button className="btn btn-sm btn-outline-primary py-0 text-start" onClick={() => viewDocument(item.loanProofPath)}>
                          <i className="bi bi-file-earmark-text me-2"></i>Loan/PIA Proof
                        </button>
                      )}
                      {item.idProofPath && (
                        <button className="btn btn-sm btn-outline-dark py-0 text-start" onClick={() => viewDocument(item.idProofPath)}>
                          <i className="bi bi-person-badge me-2"></i>Identity Proof
                        </button>
                      )}
                      {item.artisanCardPath ? (
                        <button className="btn btn-sm btn-outline-secondary py-0 text-start" onClick={() => viewDocument(item.artisanCardPath)}>
                          <i className="bi bi-card-heading me-2"></i>Artisan Card
                        </button>
                      ) : (
                         <span className="text-muted small text-center">- No Artisan Card -</span>
                      )}
                    </div>
                  </td>

                  {/* 6. Product Photos (Grid) */}
                  <td>
                    <div className="d-flex gap-1 flex-wrap justify-content-center">
                      {item.productPhotosPaths ? (
                        item.productPhotosPaths.split(',').map((path, idx) => (
                          <img 
                            key={idx}
                            src={`${IMG_BASE_URL}${path}`} 
                            alt={`Product ${idx}`} 
                            className="rounded border shadow-sm"
                            style={{ width: '45px', height: '45px', objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => viewDocument(path)} 
                            title="Click to view full size"
                            onError={(e) => {e.target.style.display = 'none'}} 
                          />
                        ))
                      ) : (
                        <span className="text-muted small">No Photos</span>
                      )}
                    </div>
                  </td>

                  {/* 7. Submission Date */}
                  <td className="text-center small text-muted">
                    <div className="fw-bold text-dark">{item.submissionDate ? new Date(item.submissionDate).toLocaleDateString() : '-'}</div>
                    <div>{item.submissionDate ? new Date(item.submissionDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}</div>
                  </td>
                </tr>
              ))}
              
              {/* Empty State */}
              {data.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No exhibition nominations received yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExhibitionsTable;